import { ethers } from "ethers";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ToastContainer, cssTransition } from "react-toastify";
import { Hash } from "viem";
import { useDisconnect } from "wagmi";

import { SUPPORTED_CHAIN_IDS, UiSupportedChain } from "config/chains";
import {
  CURRENT_PROVIDER_LOCALSTORAGE_KEY,
  REFERRAL_CODE_KEY,
  SHOULD_EAGER_CONNECT_LOCALSTORAGE_KEY,
} from "config/localStorage";
import { TOAST_AUTO_CLOSE_TIME } from "config/ui";
import { useAuth } from "context/AuthContext/AuthProvider";
import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { upsertTrader } from "lib/apis/referral";
import { trackWalletLocationEvent } from "lib/apis/walletLocationEvents";
import { clearAccessToken } from "lib/auth";
import { useRealChainIdWarning } from "lib/chains/useRealChainIdWarning";
import { applyIpLocale } from "lib/i18n";
import { REFERRAL_CODE_QUERY_PARAM, getAppBaseUrl, isHomeSite } from "lib/legacy";
import { useAccountInitedMetric, useOpenAppMetric } from "lib/metrics";
import { useConfigureMetrics } from "lib/metrics/useConfigureMetrics";
import { LandingPageAgreementConfirmationEvent } from "lib/userAnalytics/types";
import { useConfigureUserAnalyticsProfile } from "lib/userAnalytics/useConfigureUserAnalyticsProfile";
import { userAnalytics } from "lib/userAnalytics/UserAnalytics";
import { useWalletConnectedUserAnalyticsEvent } from "lib/userAnalytics/useWalletConnectedEvent";
import useRouteQuery from "lib/useRouteQuery";
import useSearchParams from "lib/useSearchParams";
import { switchNetwork } from "lib/wallets";
import useWallet from "lib/wallets/useWallet";
import { decodeReferralCode, encodeReferralCode } from "sdk/utils/referrals";

import EventToastContainer from "components/EventToast/EventToastContainer";
import useEventToast from "components/EventToast/useEventToast";
import { Header } from "components/Header/Header";
import { RedirectPopupModal } from "components/ModalViews/RedirectModal";
import { NotifyModal } from "components/NotifyModal/NotifyModal";
import { ReferralJoinModal } from "components/Referrals/ReferralJoinModal";
import { SettingsModal } from "components/SettingsModal/SettingsModal";

import { HomeRoutes } from "./HomeRoutes";
import { MainRoutes } from "./MainRoutes";

const Zoom = cssTransition({
  enter: "zoomIn",
  exit: "zoomOut",
  appendPosition: false,
  collapse: true,
  collapseDuration: 200,
});

export function AppRoutes() {
  const { disconnect } = useDisconnect();
  const isHome = isHomeSite();
  const { account, active, chainId } = useWallet();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const syncedConnectedWalletsRef = useRef<Set<string>>(new Set());

  useEventToast();
  useConfigureMetrics();
  useConfigureUserAnalyticsProfile();
  useOpenAppMetric();
  useAccountInitedMetric();
  useWalletConnectedUserAnalyticsEvent();

  const query = useRouteQuery();

  useEffect(() => {
    let referralCode = query.get(REFERRAL_CODE_QUERY_PARAM);
    const rawHashRoute = window.location.hash.startsWith("#/") ? window.location.hash.slice(1) : "";
    const [hashPathname, hashQuery] = rawHashRoute.split("?");
    const hashParams = hashQuery ? new URLSearchParams(hashQuery) : undefined;

    if (!referralCode || referralCode.length === 0) {
      const params = new URLSearchParams(window.location.search);
      referralCode = params.get(REFERRAL_CODE_QUERY_PARAM);
    }
    if (!referralCode || referralCode.length === 0) {
      if (hashQuery) {
        const hashParams = new URLSearchParams(hashQuery);
        referralCode = hashParams.get(REFERRAL_CODE_QUERY_PARAM);
      }
    }

    if (referralCode && referralCode.length <= 20) {
      const encodedReferralCode = encodeReferralCode(referralCode);
      if (encodedReferralCode !== ethers.ZeroHash) {
        localStorage.setItem(REFERRAL_CODE_KEY, encodedReferralCode);
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.has(REFERRAL_CODE_QUERY_PARAM)) {
          queryParams.delete(REFERRAL_CODE_QUERY_PARAM);
        }

        const mergedParams = new URLSearchParams(queryParams);
        hashParams?.forEach((value, key) => {
          if (!mergedParams.has(key)) {
            mergedParams.set(key, value);
          }
        });
        mergedParams.delete(REFERRAL_CODE_QUERY_PARAM);

        if (hashPathname) {
          history.replace({
            pathname: hashPathname,
            search: mergedParams.toString(),
          });
          return;
        }

        if (location.search.includes(REFERRAL_CODE_QUERY_PARAM)) {
          history.replace({
            search: mergedParams.toString(),
          });
        }
      }
    }
  }, [query, history, location.search]);

  // Guarded writes: only after SIWE session matches connected wallet.
  useEffect(() => {
    if (isHome || !active || !account || !chainId || !isAuthenticated) {
      return;
    }

    const syncKey = account.toLowerCase();
    if (syncedConnectedWalletsRef.current.has(syncKey)) {
      return;
    }
    syncedConnectedWalletsRef.current.add(syncKey);

    Promise.allSettled([
      upsertTrader({ address: account }),
      trackWalletLocationEvent({ walletAddress: account, chainId }),
    ])
      .then((results) => {
        const [traderResult, locationResult] = results;

        if (locationResult.status === "fulfilled") {
          void applyIpLocale(locationResult.value.countryCode).catch((error) => {
            // A locale catalog failure must not turn a successful wallet-location sync into an unhandled rejection.
            // eslint-disable-next-line no-console
            console.warn("Failed to activate IP-derived locale", error);
          });
        }

        if (traderResult.status === "rejected" || locationResult.status === "rejected") {
          let error: unknown;
          if (traderResult.status === "rejected") {
            error = traderResult.reason;
          } else if (locationResult.status === "rejected") {
            error = locationResult.reason;
          }
          syncedConnectedWalletsRef.current.delete(syncKey);
          // eslint-disable-next-line no-console
          console.warn("Failed to sync connected wallet to backend", error);
        }
      })
      .catch((error) => {
        syncedConnectedWalletsRef.current.delete(syncKey);
        // eslint-disable-next-line no-console
        console.warn("Failed to sync connected wallet to backend", error);
      });
  }, [isHome, active, account, chainId, isAuthenticated]);

  // Allow re-sync after sign-out / account switch.
  useEffect(() => {
    if (!isAuthenticated && account) {
      syncedConnectedWalletsRef.current.delete(account.toLowerCase());
    }
  }, [isAuthenticated, account]);

  const disconnectAccountAndCloseSettings = () => {
    disconnect();
    clearAccessToken();
    localStorage.removeItem(SHOULD_EAGER_CONNECT_LOCALSTORAGE_KEY);
    localStorage.removeItem(CURRENT_PROVIDER_LOCALSTORAGE_KEY);
    setIsSettingsVisible(false);
  };

  const [redirectModalVisible, setRedirectModalVisible] = useState(false);
  const [shouldHideRedirectModal, setShouldHideRedirectModal] = useState(false);

  const [selectedToPage, setSelectedToPage] = useState("");
  const { isSettingsVisible, setIsSettingsVisible } = useSettings();

  const openSettings = useCallback(() => {
    setIsSettingsVisible(true);
    if (import.meta.env.DEV) {
      // window.alert("Settings modal opened (gear icon detected).");
    }
  }, [setIsSettingsVisible]);

  const localStorageCode = window.localStorage.getItem(REFERRAL_CODE_KEY);
  const baseUrl = getAppBaseUrl();
  let appRedirectUrl = baseUrl + selectedToPage;
  if (localStorageCode && localStorageCode.length > 0 && localStorageCode !== ethers.ZeroHash) {
    const decodedRefCode = decodeReferralCode(localStorageCode as Hash);
    if (decodedRefCode) {
      appRedirectUrl = `${appRedirectUrl}?ref=${decodedRefCode}`;
    }
  }

  const showRedirectModal = useCallback((to: string) => {
    userAnalytics.pushEvent<LandingPageAgreementConfirmationEvent>({
      event: "LandingPageAction",
      data: {
        action: "AgreementConfirmationDialogShown",
      },
    });
    setRedirectModalVisible(true);
    setSelectedToPage(to);
  }, []);

  const urlParams = useSearchParams<{ chainId: string }>();

  useEffect(() => {
    const chainId = urlParams.chainId;
    if (chainId && SUPPORTED_CHAIN_IDS.includes(Number(chainId) as UiSupportedChain)) {
      switchNetwork(Number(chainId), true).then(() => {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.delete("chainId");
        history.replace({
          pathname: history.location.pathname,
          search: searchParams.toString(),
        });
      });
    }
  }, [urlParams, history]);

  useRealChainIdWarning();

  return (
    <>
      <div className="App">
        <div className="App-content">
          <Header
            disconnectAccountAndCloseSettings={disconnectAccountAndCloseSettings}
            openSettings={openSettings}
            showRedirectModal={showRedirectModal}
          />
          {isHome && <HomeRoutes showRedirectModal={showRedirectModal} />}
          {!isHome && <MainRoutes openSettings={openSettings} />}
        </div>
      </div>
      <ToastContainer
        limit={1}
        transition={Zoom}
        position="bottom-right"
        autoClose={TOAST_AUTO_CLOSE_TIME}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        draggable={false}
        pauseOnHover
        theme="dark"
        icon={false}
      />
      <EventToastContainer />
      <RedirectPopupModal
        redirectModalVisible={redirectModalVisible}
        setRedirectModalVisible={setRedirectModalVisible}
        appRedirectUrl={appRedirectUrl}
        setShouldHideRedirectModal={setShouldHideRedirectModal}
        shouldHideRedirectModal={shouldHideRedirectModal}
      />
      {!isHome && <ReferralJoinModal />}
      <SettingsModal isSettingsVisible={isSettingsVisible} setIsSettingsVisible={setIsSettingsVisible} />
      <NotifyModal />
    </>
  );
}
